import { useQuery } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import type { JSX } from 'react';
import type { PersonnelDetailsDto, TrialPersonnelDto } from '@ctms/contracts';

import { personnelLabel } from '../../trials/personnel-select/personnel-select.js';
import { trialsApi } from '../../trials/trials.api.js';
import {
  PERSONNEL_CARD_LIST_QUERY,
  trialQueryKeys,
} from '../../trials/trials.query-keys.js';

const labelClass = 'text-xs text-slate-400';

export interface VisitPersonnelSectionProps {
  /** The trial of this visit — the picker reads ITS site roles and team. */
  trialId: number;
  /** Currently checked `trial_personnel` ids (controlled by the parent). */
  selected: ReadonlySet<number>;
  /** Checks/unchecks one assignment. */
  onToggle: (trialPersonnelId: number) => void;
  disabled: boolean;
}

/**
 * R8 (REQ-VIS-027, #32): the visit personnel — site-staff roles declared in
 * the trial and, under each role, checkboxes of its ACTIVE people; any number
 * per role, all equal (no "main person"). Data comes from Domain 2 queries
 * (roles, team, contact cards for the names) — the register itself travels
 * with the registration save in the parent (set-replace PUT).
 */
export function VisitPersonnelSection({
  trialId,
  selected,
  onToggle,
  disabled,
}: VisitPersonnelSectionProps): JSX.Element {
  const retry = (count: number, error: unknown): boolean =>
    isHTTPError(error) ? false : count < 3;

  const rolesQuery = useQuery({
    queryKey: trialQueryKeys.roles(trialId),
    queryFn: () => trialsApi.listRoles(trialId),
    staleTime: 5 * 60 * 1000,
    retry,
  });
  const teamQuery = useQuery({
    queryKey: trialQueryKeys.team(trialId),
    queryFn: () => trialsApi.listTrialPersonnel(trialId),
    staleTime: 5 * 60 * 1000,
    retry,
  });
  const cardsQuery = useQuery({
    queryKey: trialQueryKeys.personnelCards(PERSONNEL_CARD_LIST_QUERY),
    queryFn: () => trialsApi.listPersonnel(PERSONNEL_CARD_LIST_QUERY),
    staleTime: 5 * 60 * 1000,
    retry,
  });

  const cardsById = new Map((cardsQuery.data?.items ?? []).map((card) => [card.id, card]));
  const labelOf = (assignment: TrialPersonnelDto): string => {
    const card: PersonnelDetailsDto | undefined = cardsById.get(assignment.personnelDetailsId);
    return card ? personnelLabel(card) : `Osoba #${assignment.personnelDetailsId}`;
  };

  // Site roles only (Piotr 2026-10-20) — external roles (CRA/MM) stay hidden.
  const siteRoles = (rolesQuery.data ?? []).filter((role) => role.siteStaff);
  const loading = rolesQuery.isPending || teamQuery.isPending || cardsQuery.isPending;
  const failed = rolesQuery.isError || teamQuery.isError || cardsQuery.isError;

  return (
    <fieldset>
      <legend className={labelClass}>Personel wizyty</legend>
      <p className="mt-1 text-xs text-slate-500">
        Opcjonalnie — zaznacz osoby, które przeprowadziły wizytę. Przy jednej roli może być
        dowolna liczba równorzędnych osób.
      </p>
      {loading ? (
        <p className="mt-2 text-xs text-slate-400">Wczytywanie zespołu badania…</p>
      ) : failed ? (
        <p className="mt-2 text-xs text-rose-400">Nie udało się wczytać zespołu badania.</p>
      ) : (
        // Scrollable pane: the first view fits ~3 roles with 2–3 names, the
        // rest scrolls (fast-checkbox-ui).
        <div className="mt-2 max-h-56 space-y-3 overflow-y-auto pr-1">
          {siteRoles.map((role) => {
            const people = (teamQuery.data ?? [])
              .filter((assignment) => assignment.roleId === role.id && assignment.active)
              .sort((a, b) => labelOf(a).localeCompare(labelOf(b), 'pl'));
            return (
              <div key={role.id}>
                <p className="text-xs font-semibold text-slate-200">{role.name}</p>
                {people.length === 0 ? (
                  <p className="mt-1 text-xs text-slate-500">Brak aktywnych osób przy tej roli</p>
                ) : (
                  <ul className="mt-1 space-y-1">
                    {people.map((assignment) => (
                      <li key={assignment.id}>
                        <label className="flex items-center gap-2 text-xs text-slate-200">
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 accent-emerald-600"
                            checked={selected.has(assignment.id)}
                            onChange={() => onToggle(assignment.id)}
                            disabled={disabled}
                          />
                          {labelOf(assignment)}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {siteRoles.length === 0 ? (
            <p className="text-xs text-slate-500">Badanie nie ma zadeklarowanych ról ośrodkowych.</p>
          ) : null}
        </div>
      )}
    </fieldset>
  );
}
